import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography
} from '@mui/material';

const EditBatteryDialog = ({ open, onClose, onEdit, battery }) => {
  const [formData, setFormData] = useState({
    type: '',
    customType: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    nickname: '',
    capacity: '',
    voltage: '',
    cycleCount: '0',
    healthPercentage: '100',
    status: 'active',
    purchaseDate: '',
    lastChargedDate: '',
    compatibleDroneModels: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  const typeOptions = [
    { value: 'lipo', label: 'LiPo (Lithium Polymer)' },
    { value: 'liion', label: 'Li-ion (Lithium Ion)' },
    { value: 'lihv', label: 'LiHV (Lithium High Voltage)' },
    { value: 'nimh', label: 'NiMH (Nickel-Metal Hydride)' },
    { value: 'other', label: 'Other' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'charging', label: 'Charging' },
    { value: 'storage', label: 'Storage' },
    { value: 'retired', label: 'Retired' },
    { value: 'damaged', label: 'Damaged' }
  ];

  // Populate form with existing battery data
  useEffect(() => {
    if (battery) {
      // Check if battery type is in predefined options
      const isPredefinedType = typeOptions.some(opt => opt.value === battery.type);

      setFormData({
        type: isPredefinedType ? battery.type : 'other',
        customType: !isPredefinedType && battery.type ? battery.type : '',
        manufacturer: battery.manufacturer || '',
        model: battery.model || '',
        serialNumber: battery.serialNumber || '',
        nickname: battery.nickname || '',
        capacity: battery.capacity?.toString() || '',
        voltage: battery.voltage?.toString() || '',
        cycleCount: battery.cycleCount?.toString() || '0',
        healthPercentage: battery.healthPercentage?.toString() || '100',
        status: battery.status || 'active',
        purchaseDate: battery.purchaseDate ? new Date(battery.purchaseDate).toISOString().split('T')[0] : '',
        lastChargedDate: battery.lastChargedDate ? new Date(battery.lastChargedDate).toISOString().split('T')[0] : '',
        compatibleDroneModels: battery.compatibleDroneModels || '',
        notes: battery.notes || ''
      });
    }
  }, [battery]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.type?.trim()) {
      newErrors.type = 'Battery type is required';
    }
    if (formData.type === 'other' && !formData.customType?.trim()) {
      newErrors.customType = 'Custom type is required when Other is selected';
    }
    if (!formData.manufacturer?.trim()) {
      newErrors.manufacturer = 'Manufacturer is required';
    }
    if (!formData.model?.trim()) {
      newErrors.model = 'Model is required';
    }
    if (!formData.serialNumber?.trim()) {
      newErrors.serialNumber = 'Serial number is required';
    }
    if (!formData.capacity || formData.capacity <= 0) {
      newErrors.capacity = 'Valid capacity is required';
    }
    if (!formData.voltage || formData.voltage <= 0) {
      newErrors.voltage = 'Valid voltage is required';
    }
    if (formData.healthPercentage < 0 || formData.healthPercentage > 100) {
      newErrors.healthPercentage = 'Health must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const dataToSubmit = {
      ...formData,
      type: formData.type === 'other' ? formData.customType.trim() : formData.type,
      capacity: parseInt(formData.capacity),
      voltage: parseFloat(formData.voltage),
      cycleCount: parseInt(formData.cycleCount) || 0,
      healthPercentage: parseInt(formData.healthPercentage) || 100,
      nickname: formData.nickname?.trim() || null,
      purchaseDate: formData.purchaseDate || null,
      lastChargedDate: formData.lastChargedDate || null,
      compatibleDroneModels: formData.compatibleDroneModels?.trim() || null,
      notes: formData.notes?.trim() || null
    };

    // Remove customType from submission as it's merged into type
    delete dataToSubmit.customType;

    onEdit(battery.id, dataToSubmit);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Battery</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              select
              label="Battery Type"
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
              error={!!errors.type}
              helperText={errors.type}
            >
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {formData.type === 'other' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Custom Battery Type"
                value={formData.customType}
                onChange={(e) => handleChange('customType', e.target.value)}
                error={!!errors.customType}
                helperText={errors.customType || 'Enter custom battery type'}
                placeholder="e.g., LiFePO4, Custom Chemistry"
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Manufacturer"
              value={formData.manufacturer}
              onChange={(e) => handleChange('manufacturer', e.target.value)}
              error={!!errors.manufacturer}
              helperText={errors.manufacturer}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Model"
              value={formData.model}
              onChange={(e) => handleChange('model', e.target.value)}
              error={!!errors.model}
              helperText={errors.model}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Serial Number"
              value={formData.serialNumber}
              onChange={(e) => handleChange('serialNumber', e.target.value)}
              error={!!errors.serialNumber}
              helperText={errors.serialNumber}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nickname (Optional)"
              value={formData.nickname}
              onChange={(e) => handleChange('nickname', e.target.value)}
            />
          </Grid>

          {/* Technical Specifications */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Technical Specifications
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label="Capacity (mAh)"
              value={formData.capacity}
              onChange={(e) => handleChange('capacity', e.target.value)}
              error={!!errors.capacity}
              helperText={errors.capacity}
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label="Voltage (V)"
              value={formData.voltage}
              onChange={(e) => handleChange('voltage', e.target.value)}
              error={!!errors.voltage}
              helperText={errors.voltage}
              inputProps={{ min: 0, step: 0.1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Cycle Count"
              value={formData.cycleCount}
              onChange={(e) => handleChange('cycleCount', e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Health Percentage"
              value={formData.healthPercentage}
              onChange={(e) => handleChange('healthPercentage', e.target.value)}
              error={!!errors.healthPercentage}
              helperText={errors.healthPercentage || 'Battery health (0-100%)'}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>

          {/* Status and Dates */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Status and Dates
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Status"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Purchase Date"
              value={formData.purchaseDate}
              onChange={(e) => handleChange('purchaseDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Last Charged Date"
              value={formData.lastChargedDate}
              onChange={(e) => handleChange('lastChargedDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Additional Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Compatible Drone Models"
              value={formData.compatibleDroneModels}
              onChange={(e) => handleChange('compatibleDroneModels', e.target.value)}
              helperText="Comma-separated list of compatible drone models"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBatteryDialog;
